// Helpers for converting between GeoJSON and Google Maps paths, used by the
// GeoJSON zone management UI (create modal, zone list previews).

export type LatLng = { lat: number; lng: number }

/**
 * Parse a GeoJSON string, returning the object or null when invalid JSON.
 */
export const safeParseGeojson = (text: string): any | null => {
	try {
		const parsed = JSON.parse(text)
		return parsed && typeof parsed === "object" ? parsed : null
	} catch {
		return null
	}
}

/**
 * Extract the outer rings of every Polygon/MultiPolygon feature as Google Maps
 * paths ({lat,lng}[]), for rendering a preview. Accepts a bare geometry, a
 * Feature, or a FeatureCollection. Returns [] when nothing usable is found.
 */
export const geojsonToPolygonPaths = (geojson: any): LatLng[][] => {
	if (!geojson || typeof geojson !== "object") return []

	let features: any[] = []
	if (geojson.type === "FeatureCollection") {
		features = Array.isArray(geojson.features) ? geojson.features : []
	} else if (geojson.type === "Feature") {
		features = [geojson]
	} else if (geojson.type === "Polygon" || geojson.type === "MultiPolygon") {
		features = [{ type: "Feature", geometry: geojson, properties: {} }]
	}

	const paths: LatLng[][] = []
	const ringToPath = (ring: any): LatLng[] =>
		(Array.isArray(ring) ? ring : [])
			.filter((c: any) => Array.isArray(c) && c.length >= 2)
			.map((c: any) => ({ lat: c[1], lng: c[0] }))

	for (const feature of features) {
		const geometry = feature?.geometry
		if (!geometry || !Array.isArray(geometry.coordinates)) continue

		if (geometry.type === "Polygon") {
			// coordinates: [outerRing, ...holes] — preview the outer ring.
			paths.push(ringToPath(geometry.coordinates[0]))
		} else if (geometry.type === "MultiPolygon") {
			// coordinates: [[outerRing, ...holes], ...] — preview each outer ring.
			for (const poly of geometry.coordinates) {
				paths.push(ringToPath(poly?.[0]))
			}
		}
	}

	return paths.filter((p) => p.length > 0)
}

/**
 * Convert a list of delivery zones (each with a `geojson`) into styled polygon
 * objects for the GoogleMaps component. Shared by the order maps so delivery
 * areas render consistently (multi-order map, single-order map, address
 * validation map).
 */
export const deliveryZonesToPolygons = (deliveryZones?: any[]): any[] => {
	return (deliveryZones || []).flatMap((zone: any) =>
		geojsonToPolygonPaths(zone?.geojson).map((paths) => ({
			paths,
			fillColor: "#75e36d",
			fillOpacity: 0.15,
			strokeColor: "#52C41A",
			strokeOpacity: 0.9,
			strokeWeight: 2.5,
		}))
	)
}

/**
 * Build a FeatureCollection with a single Polygon from a drawn Google Maps path.
 * Closes the ring (first point repeated at the end) as required by GeoJSON.
 */
export const pathToFeatureCollection = (path: LatLng[]): any => {
	const ring = path.map((p) => [p.lng, p.lat])
	if (ring.length > 0) {
		const first = ring[0]
		const last = ring[ring.length - 1]
		if (first[0] !== last[0] || first[1] !== last[1]) {
			ring.push([first[0], first[1]])
		}
	}

	return {
		type: "FeatureCollection",
		features: [
			{
				type: "Feature",
				properties: {},
				geometry: { type: "Polygon", coordinates: [ring] },
			},
		],
	}
}

// Synthetic id for the automatic "Uber limits" zone. It is not a GeojsonZones
// document — the API resolves it against the contract's Uber pickup point.
// Keep in sync with UBER_GEO_ZONE_ID in dingoo-api getOrders.js.
export const UBER_GEO_ZONE_ID = "uber"

export type UberDeliveryArea = { lat: number; lng: number; radiusKm: number }

// Vertices used to approximate a circle. High enough that the drawn ring reads
// as a circle at any zoom the orders map allows.
const CIRCLE_SEGMENTS = 128

/**
 * Approximate a circle of `radiusKm` around `center` as a GeoJSON
 * FeatureCollection with a single Polygon. Uses an equirectangular
 * approximation, which is accurate at the few-kilometre scale these zones use.
 */
export const circleToFeatureCollection = (
	center: LatLng,
	radiusKm: number
): any => {
	const EARTH_RADIUS_KM = 6371
	const latRadius = (radiusKm / EARTH_RADIUS_KM) * (180 / Math.PI)
	// Longitude degrees shrink towards the poles.
	const lngRadius = latRadius / Math.cos((center.lat * Math.PI) / 180)

	const ring: number[][] = []
	for (let i = 0; i <= CIRCLE_SEGMENTS; i++) {
		const angle = (i / CIRCLE_SEGMENTS) * 2 * Math.PI
		ring.push([
			center.lng + lngRadius * Math.cos(angle),
			center.lat + latRadius * Math.sin(angle),
		])
	}

	return {
		type: "FeatureCollection",
		features: [
			{
				type: "Feature",
				properties: {},
				geometry: { type: "Polygon", coordinates: [ring] },
			},
		],
	}
}

// A contract's zones are presented as one list, but persist as two fields:
// "deliveryArea" rows are the contract's `deliveryZones` (a pure delivery-area
// check), the rest are `deliveryModeZones` entries applied at order import.
export type ZoneMode = "deliveryArea" | "onlyVan" | "onlyBike"

export type ContractZoneRow = { zone: string; mode: ZoneMode }

export const ZONE_MODES: ZoneMode[] = ["deliveryArea", "onlyVan", "onlyBike"]

export const ZONE_MODE_COLORS: Record<ZoneMode, string> = {
	deliveryArea: "#52C41A",
	onlyVan: "#1C70D9",
	onlyBike: "#D9871C",
}

const zoneId = (value: any): string =>
	typeof value === "string" ? value : value?._id || ""

/**
 * Merge a contract's two persisted zone fields into the single list the UI edits.
 * A contract saved before delivery modes existed yields delivery-area rows only.
 */
export const contractZonesToRows = (contract: any): ContractZoneRow[] => {
	const rows: ContractZoneRow[] = (contract?.deliveryZones || [])
		.map((z: any) => ({ zone: zoneId(z), mode: "deliveryArea" as ZoneMode }))
		.filter((row: ContractZoneRow) => row.zone)

	for (const entry of contract?.deliveryModeZones || []) {
		const id = zoneId(entry?.zone)
		if (id && entry?.mode) rows.push({ zone: id, mode: entry.mode })
	}

	return rows
}

/** The `deliveryZones` half of the edited list: ids of the delivery-area rows. */
export const rowsToDeliveryZones = (rows: ContractZoneRow[]): string[] =>
	rows
		.filter((row) => row.mode === "deliveryArea" && row.zone)
		.map((r) => r.zone)

/** The `deliveryModeZones` half: every row carrying a non-delivery-area mode. */
export const rowsToDeliveryModeZones = (
	rows: ContractZoneRow[]
): { zone: string; mode: ZoneMode }[] =>
	rows
		.filter((row) => row.mode !== "deliveryArea" && row.zone)
		.map((row) => ({ zone: row.zone, mode: row.mode }))

/**
 * Convert the selected filter zone into a styled polygon for the GoogleMaps
 * component. Deliberately styled apart from the contract's delivery zones
 * (green) so it reads as "the area you are filtering by".
 */
export const filterZoneToPolygons = (
	zone?: { geojson?: any } | null
): any[] => {
	return geojsonToPolygonPaths(zone?.geojson).map((paths) => ({
		paths,
		fillColor: "#1890FF",
		fillOpacity: 0.08,
		strokeColor: "#1890FF",
		strokeOpacity: 0.9,
		strokeWeight: 2,
	}))
}
